type EditorFieldSource = "ai" | "context" | "manual" | "derived";
type EditorFieldBadgeTone = "accent" | "neutral" | "outline";
type EditorFieldGuardrailIntent = "heroHeadline" | "heroSubheadline";
type EditorFieldSeoSignal = "critical" | "supporting";

interface EditorFieldHydrationPolicy {
  overridePolicy?: "userWins" | "systemWins";
}

interface EditorFieldGuardrails {
  intent?: EditorFieldGuardrailIntent;
  minLength?: number;
  maxLength?: number;
  requireLocation?: boolean;
  requireService?: boolean;
  disallowSubstrings?: string[];
  genericPhrases?: string[];
  maxWarnings?: number;
}

interface EditorFieldMetadata {
  source?: EditorFieldSource;
  description?: string;
  seo?: boolean;
  promptGuidance?: string[];
  hydration?: EditorFieldHydrationPolicy;
  guardrails?: EditorFieldGuardrails;
}

interface EditorFieldBadge {
  label: string;
  tone: EditorFieldBadgeTone;
}

interface EditorFieldGuidance {
  badges: EditorFieldBadge[];
  description?: string;
  reassurance?: string;
  tips: string[];
}

interface EditorFieldSeoIndicator {
  signal: EditorFieldSeoSignal;
  label: string;
  description: string;
}

const cleanText = (value?: string): string | undefined => {
    if (typeof value !== "string") {
      return undefined;
    }

    const trimmed = value.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
    return undefined;
  },
  cleanTips = (values?: string[]): string[] => {
    if (!Array.isArray(values)) {
      return [];
    }

    return values
      .map((value) => cleanText(value))
      .filter((value): value is string => Boolean(value))
      .slice(0, 2);
  },

  getEditorFieldGuidance = (metadata?: EditorFieldMetadata): EditorFieldGuidance => {
    const badges: EditorFieldBadge[] = [],
      description = cleanText(metadata?.description),
      source = metadata?.source,
      tips = cleanTips(metadata?.promptGuidance);

    if (source === "ai") {
      badges.push({ label: "AI draft", tone: "accent" });
    }

    if (source === "context") {
      badges.push({ label: "Business info", tone: "neutral" });
    }

    if (source === "derived") {
      badges.push({ label: "Auto-filled", tone: "neutral" });
    }

    if (metadata?.seo === true) {
      badges.push({ label: "SEO support", tone: "outline" });
    }

    let reassurance: string | undefined;

    switch (source) {
      case "ai": {
        reassurance = "AI draft. Edit freely.";
        break;
      }
      case "context": {
        reassurance = "From your business info. Review for accuracy.";
        break;
      }
      case "derived": {
        reassurance = "Usually handled for you. Change only if needed.";
        break;
      }
      default: {
        if (metadata?.seo === true) {
          reassurance = "Write naturally.";
        }
        break;
      }
    }

    if (metadata?.hydration?.overridePolicy === "userWins") {
      if (reassurance === undefined) {
        reassurance = "Your edits stay.";
      } else {
        reassurance = `${reassurance} Your edits stay.`;
      }
    }

    return {
      badges,
      description,
      reassurance,
      tips,
    };
  },
  getEditorFieldSeoIndicator = (
    metadata?: EditorFieldMetadata,
  ): EditorFieldSeoIndicator | null => {
    if (metadata?.guardrails) {
      return {
        description:
          "This field has stronger SEO impact. Edit freely, but keep the main service and location natural when they fit.",
        label: "SEO-sensitive",
        signal: "critical",
      };
    }

    if (metadata?.seo === true) {
      return {
        description:
          "This field supports the page's search relevance, but it does not need the same guardrails as your primary headline.",
        label: "Supports SEO",
        signal: "supporting",
      };
    }

    return null;
  };

export type {
  EditorFieldSource,
  EditorFieldBadgeTone,
  EditorFieldGuardrailIntent,
  EditorFieldSeoSignal,
  EditorFieldHydrationPolicy,
  EditorFieldGuardrails,
  EditorFieldMetadata,
  EditorFieldBadge,
  EditorFieldGuidance,
  EditorFieldSeoIndicator,
};
export { getEditorFieldGuidance, getEditorFieldSeoIndicator };
